/**
 * Versioned localStorage store backing the Assisted eKYC showcase.
 * Specified in contracts-1 §11.3.
 *
 * The whole point of persisting to localStorage rather than holding state in memory is the
 * maker → checker → COGB handoff: a request created by `test-maker` must still be sitting in
 * BRANCH_REVIEW after logging out and back in as `test-checker`.
 *
 * Chunk 1 seeds only `users` and `menus`. Chunks 3–5 add `ekycRequests`, `serviceRequests` and the
 * configuration collections — bump MOCK_DB_SCHEMA_VERSION when the seed shape changes so existing
 * browsers reseed instead of running against a stale store.
 */

import { MockUser } from './fixtures/users.fixture';
import { buildMenuTree, MockMenuNode, MockRole } from './fixtures/menus.fixture';

export const MOCK_DB_KEY = 'ncc-assisted-ekyc-mock-db';
export const MOCK_DB_SESSION_KEY = 'ncc-assisted-ekyc-mock-session';
// 4 — risk grading sheet reshaped (typed questions, Sanction Screening row dropped) and the
// occupation categories reduced to Business / Profession. Bumped so already-seeded browsers
// pick the new configuration up instead of serving the stored copy of the old one.
export const MOCK_DB_SCHEMA_VERSION = 5;

interface MockDbEnvelope {
  schemaVersion: number;
  seededAt: string;
  data: Record<string, unknown>;
}

/**
 * Only genuinely mutable state belongs in the store. Users and menus are static configuration
 * read straight from their fixtures — persisting them would mean every fixture edit needs a
 * schema-version bump to take effect in an already-seeded browser.
 *
 * Chunks 3–5 populate this with `ekycRequests`, `serviceRequests` and the configuration
 * collections, which genuinely do change as a demo runs.
 */
function seed(): Record<string, unknown> {
  return {};
}

function writeEnvelope(envelope: MockDbEnvelope): void {
  localStorage.setItem(MOCK_DB_KEY, JSON.stringify(envelope));
}

function freshEnvelope(): MockDbEnvelope {
  return {
    schemaVersion: MOCK_DB_SCHEMA_VERSION,
    seededAt: new Date().toISOString(),
    data: seed(),
  };
}

function readEnvelope(): MockDbEnvelope {
  const raw = localStorage.getItem(MOCK_DB_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as MockDbEnvelope;
      if (parsed?.schemaVersion === MOCK_DB_SCHEMA_VERSION && parsed.data) {
        return parsed;
      }
    } catch {
      // Corrupt store — fall through and reseed rather than leaving the app wedged.
    }
  }
  const envelope = freshEnvelope();
  writeEnvelope(envelope);
  return envelope;
}

export const MockDb = {
  /** Read a seeded collection. Returns the seed default if the key was never written. */
  get<T>(collection: string): T {
    return readEnvelope().data[collection] as T;
  },

  /** Overwrite a collection and persist. */
  set<T>(collection: string, value: T): void {
    const envelope = readEnvelope();
    envelope.data[collection] = value as unknown;
    writeEnvelope(envelope);
  },

  /** Wipe the store and the session, then reseed from the fixtures. */
  reset(): void {
    localStorage.removeItem(MOCK_DB_KEY);
    localStorage.removeItem(MOCK_DB_SESSION_KEY);
    writeEnvelope(freshEnvelope());
  },

  /** Ensure the store exists. Safe to call repeatedly. */
  ensureSeeded(): void {
    readEnvelope();
  },

  // ---- session -------------------------------------------------------------------------------

  setCurrentUser(user: MockUser): void {
    localStorage.setItem(MOCK_DB_SESSION_KEY, JSON.stringify(user));
  },

  getCurrentUser(): MockUser | null {
    const raw = localStorage.getItem(MOCK_DB_SESSION_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as MockUser;
    } catch {
      return null;
    }
  },

  clearCurrentUser(): void {
    localStorage.removeItem(MOCK_DB_SESSION_KEY);
  },

  /** Built fresh from the fixture on every call — see the note on seed(). */
  menusFor(role: MockRole): MockMenuNode[] {
    return buildMenuTree(role);
  },
};

/**
 * Honour `?resetMock=1` on any URL. Called once from mock.providers.ts at bootstrap so a stale
 * store never blocks a demo — no need to hunt through devtools to clear it.
 */
export function applyResetMockQueryParam(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('resetMock') === '1') {
      MockDb.reset();
      // Drop the flag so a refresh does not wipe work-in-progress a second time.
      params.delete('resetMock');
      const query = params.toString();
      const url = window.location.pathname + (query ? `?${query}` : '') + window.location.hash;
      window.history.replaceState({}, '', url);
    }
  } catch {
    // Non-browser context (SSR, tests) — nothing to do.
  }
}
